import { Project, ProjectPriorityLabel } from "@constructr-sdk/entities";

/**
 * Allowed sorting strategies for the projects grid.
 * The implementation must sort on the client using current state, while ensuring the
 * canonical sort order is consistent after remote updates from TRPC.
 */
export enum ProjectsSortBy {
  LOWEST_PERCENT = "lowest_percent",
  HIGHEST_PERCENT = "highest_percent",
  LAST_ANALYZED = "last_analyzed",
  MOST_OPEN_TASKS = "most_open_tasks",
}

/**
 * State required by the Projects Dashboard view.
 */
export interface UseProjectsDashboardState {
  /**
   * Raw list of projects for the authenticated user (and active organization, if applicable).
   * Must be kept in sync with server updates.
   */
  projects: Project[];

  /**
   * Derived, stable list of projects sorted according to `sortBy`.
   * The implementation should memoize this to avoid unnecessary recomputations.
   */
  sortedProjects: Project[];

  /**
   * Current sorting strategy used by the top bar dropdown.
   */
  sortBy: ProjectsSortBy;

  /**
   * True while initial or subsequent loads are in flight.
   */
  isLoading: boolean;

  /**
   * If a load or command fails, provide a human-friendly error message here.
   * The view can display a toast and/or inline error state based on this.
   */
  error: string | null;

  /**
   * Set of project ids currently running analysis (in-flight), used to render per-button loaders and disable clicks until completion.
   */
  runningAnalysisProjectIds: string[];
}

/**
 * Mutations are synchronous client-side updates.
 * They should only update local state and must not cause infinite loops.
 */
export interface UseProjectsDashboardMutations {
  /**
   * Update the current sort option.
   * - Must not trigger remote calls.
   * - Should re-compute `sortedProjects` based on current `projects`.
   */
  setSortBy: (sortBy: ProjectsSortBy) => void;
}

/**
 * Commands may interact with the server (TRPC) and handle workflows.
 * - All multi-step workflows (e.g., analysis run + DB updates) MUST be implemented
 *   on the server side and exposed as a single TRPC endpoint.
 * - The domain hook must call those endpoints and reflect optimistic updates where appropriate.
 */
export interface UseProjectsDashboardCommands {
  /**
   * Load or reload projects from the server using TRPC data providers.
   * - Should use react-query under the hood for caching and invalidation.
   * - Must support active organization scoping if applicable.
   */
  loadProjects: () => Promise<void>;

  /**
   * Create a new project. Attributes must be passed individually (not as an object).
   * - Must validate uniqueness of name and baseUrl on the server.
   * - The implementation should optimistically add the project and reconcile on success.
   */
  createProject: (name: string, baseUrl: string) => Promise<void>;

  /**
   * Delete a project by id along with associated tasks.
   * - Must prompt a UI confirmation prior to calling (the view handles the modal).
   * - Should handle errors gracefully and update local state accordingly.
   */
  deleteProject: (projectId: string) => Promise<void>;

  /**
   * Trigger a website analysis for the given project. The server should orchestrate
   * the analysis engine + task generation as a single TRPC endpoint.
   * - The UI should display feedback (e.g., toast) driven by the command's result.
   * - On completion, the server is expected to update lastAnalysisAt and task stats.
   * - The implementation must refresh `projects` after success.
   */
  runAnalysis: (projectId: string) => Promise<void>;
}

export interface UseProjectsDashboardReturn
  extends UseProjectsDashboardState,
    UseProjectsDashboardMutations,
    UseProjectsDashboardCommands {}

export interface UseProjectsDashboardParams {
  /**
   * If true, the hook should automatically call `loadProjects()` on mount.
   */
  autoLoad?: boolean;
}

/**
 * Interface signature for the Projects Dashboard domain hook.
 * The concrete implementation will live alongside this interface and use TRPC
 * for data fetching and mutations.
 */
export type UseProjectsDashboard = (
  params: UseProjectsDashboardParams,
) => UseProjectsDashboardReturn;

/**
 * Implementation notes (for future implementers):
 * - All date fields in Project are UTC Dates and should be treated as such.
 * - Sorting rules:
 *   - LOWEST_PERCENT: ascending by percentComplete, then by name ASC.
 *   - HIGHEST_PERCENT: descending by percentComplete, then by name ASC.
 *   - LAST_ANALYZED: descending by lastAnalysisAt (nulls last), then by name ASC.
 *   - MOST_OPEN_TASKS: descending by openTasksCount, then by name ASC.
 * - Validate `createProject(name, baseUrl)`:
 *   - Name: 1..128 chars, unique per user/org.
 *   - baseUrl: valid URL, normalized (e.g., https://example.com), unique per user/org.
 */